import React, { useState, useEffect, useCallback } from 'react';
import { X, Eye, EyeOff, UserPlus, Trash2 } from 'lucide-react';
import { api } from '../../api/client';

const ManageWorkshopModal = ({ workshop, canEditSettings, onClose, onUpdated, onDeleted }) => {
  const [name, setName] = useState(workshop.name);
  const [description, setDescription] = useState(workshop.description || '');
  const [isPasswordProtected, setIsPasswordProtected] = useState(workshop.is_password_protected);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [participants, setParticipants] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loadingParticipants, setLoadingParticipants] = useState(true);

  const loadParticipants = useCallback(async () => {
    setLoadingParticipants(true);
    try {
      const [participantsData, availableData] = await Promise.all([
        api.getWorkshopParticipants(workshop.id),
        api.getAvailableWorkshopUsers(workshop.id),
      ]);
      setParticipants(participantsData);
      setAvailableUsers(availableData);
    } catch (error) {
      console.error('Failed to load participants:', error);
    } finally {
      setLoadingParticipants(false);
    }
  }, [workshop.id]);

  useEffect(() => { loadParticipants(); }, [loadParticipants]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const updated = await api.updateWorkshop(workshop.id, {
        name,
        description,
        is_password_protected: isPasswordProtected,
        workshop_password: isPasswordProtected ? (password || undefined) : null,
      });
      onUpdated(updated);
    } catch (error) {
      alert('Failed to update workshop: ' + error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!selectedUserId) return;
    try {
      await api.addWorkshopParticipant(workshop.id, parseInt(selectedUserId));
      setSelectedUserId('');
      loadParticipants();
    } catch (error) {
      alert('Failed to add participant: ' + error.message);
    }
  };

  const handleRemoveParticipant = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from this workshop?`)) return;
    try {
      await api.removeWorkshopParticipant(workshop.id, userId);
      loadParticipants();
    } catch (error) {
      alert('Failed to remove participant: ' + error.message);
    }
  };

  const handleDeleteWorkshop = async () => {
    const confirmed = window.confirm(
      `Delete "${workshop.name}"?\n\nThis will permanently delete EVERY foresight session and writing session inside this workshop, along with all their contents. This cannot be undone.\n\nType OK to confirm.`
    );
    if (!confirmed) return;
    try {
      await api.deleteWorkshop(workshop.id);
      onDeleted();
    } catch (error) {
      alert('Failed to delete workshop: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">
            {canEditSettings ? 'Workshop Settings' : 'Manage Participants'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic settings -- admins only. Facilitators can manage participants
              (below) for workshops they belong to, but not rename, re-password,
              or delete the workshop itself. */}
          {canEditSettings && (
            <>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]" />
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer mb-1">
                    <input type="checkbox" checked={isPasswordProtected}
                      onChange={e => setIsPasswordProtected(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#01006A] focus:ring-[#01006A]" />
                    <span className="text-sm font-medium text-gray-700">Password-protect this workshop</span>
                  </label>
                  {isPasswordProtected && (
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder={workshop.is_password_protected ? 'Leave blank to keep current password' : 'Set a password'}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]" />
                      <button onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings || !name || (isPasswordProtected && !workshop.is_password_protected && !password)}
                  className="w-full bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-300 text-sm font-medium">
                  {savingSettings ? 'Saving…' : 'Save Settings'}
                </button>
                {isPasswordProtected && !workshop.is_password_protected && !password && (
                  <p className="text-xs text-amber-600">Set a password before enabling protection.</p>
                )}
              </div>

              <hr className="border-gray-200" />
            </>
          )}

          {/* Participants -- open to admins, and to facilitators who are
              already members of this workshop. */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-3">Participants</h4>
            {loadingParticipants ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : (
              <div className="space-y-2 mb-4">
                {participants.map(p => (
                  <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {p.user_name}
                        {p.user_id === workshop.created_by && (
                          <span className="ml-2 text-xs text-gray-400">(creator)</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{p.user_email}</p>
                    </div>
                    {p.user_id !== workshop.created_by && (
                      <button onClick={() => handleRemoveParticipant(p.user_id, p.user_name)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-gray-100" title="Remove">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {participants.length === 0 && (
                  <p className="text-sm text-gray-400">No participants yet.</p>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#01006A]">
                <option value="">Select a user to add…</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
              <button onClick={handleAddParticipant} disabled={!selectedUserId}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] disabled:bg-gray-300 text-sm font-medium">
                <UserPlus className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>

          {/* Danger zone -- admins only */}
          {canEditSettings && (
            <>
              <hr className="border-gray-200" />
              <div>
                <button onClick={handleDeleteWorkshop}
                  className="w-full text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 py-2 rounded-lg">
                  Delete Workshop
                </button>
                <p className="text-xs text-gray-400 mt-1 text-center">
                  Permanently deletes this workshop and everything inside it.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageWorkshopModal;
